import { type ReactElement, useMemo, useRef } from 'react';
import { useCategoryTreeQuery } from '@/entities/category';
import { useLandingHeroImagesQuery } from '@/entities/landing-image';
import { useLandingPartnersQuery } from '@/entities/landing-partner';
import { useLandingTestimonialsQuery } from '@/entities/landing-testimonial';
import { useClientLogosQuery, useHomePortfoliosQuery } from '@/entities/portfolio';
import { useCategoryCoverImagesQuery, useHomeRecommendedProductsQuery } from '@/entities/product';
import { useScrolledAbove } from '@/shared/lib';
import { HomeBestsellers } from '@/widgets/home-bestsellers';
import { HomeBrandBanner } from '@/widgets/home-brand-banner';
import { HOME_CATEGORY_GROUPS, HomeCategories } from '@/widgets/home-categories';
import { HomeClientLogos } from '@/widgets/home-client-logos';
import { HomeFeatured } from '@/widgets/home-featured';
import { HomeHero } from '@/widgets/home-hero';
import { HomePortfolio } from '@/widgets/home-portfolio';
import { HomeTestimonials } from '@/widgets/home-testimonials';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/**
 * HOME(/) 랜딩.
 * 첫 화면은 공지 띠 아래 화면 높이의 히어로 배너 슬라이더이고, 흰 로고·메뉴가 사진 위에 얹혀 있다.
 * 히어로는 admin 배너(배너마다 연결 상품)를 쓰고, 배너가 없을 때만 추천 목록으로 사진 없는 슬라이드를 만든다.
 * 사진 위 로고·메뉴가 스크롤로 지나가면 화면 맨 위에 로고 줄이 고정되고, 히어로를 지나면 흰 바탕으로 바뀐다.
 * 순서: 히어로·추천 상품(네 장씩 넘김)·카테고리(대분류 세 칸)·이달의 상품·포트폴리오(선택된 사례가 있을 때만)·
 * 기업 고객 사례(운영자가 등록한 것이 있을 때만)·
 * 함께한 브랜드(운영자 등록 기업 로고가 있으면 그것, 없으면 로고 있는 사례가 있을 때만)·브랜드 배너·푸터.
 * 추천 상품·이달의 상품·브랜드 배너는 같은 추천 목록을 쓴다. 카테고리 대표 사진은 대분류마다 추천순 첫 상품 사진이다.
 */
export const HomePage = (): ReactElement => {
  const productsQuery = useHomeRecommendedProductsQuery();
  const products = productsQuery.data ?? [];
  const bannersQuery = useLandingHeroImagesQuery();
  const portfolioQuery = useHomePortfoliosQuery();
  const categoriesQuery = useCategoryTreeQuery();
  const categoryGroups = categoriesQuery.data ?? [];
  // 대표 사진은 보여 줄 세 대분류만 묻는다 — id 목록이 같으면 같은 배열을 유지해 재조회를 막는다.
  const coverCategoryIds = useMemo(
    () => categoryGroups.slice(0, HOME_CATEGORY_GROUPS).map((group) => group.id),
    [categoryGroups],
  );
  const categoryCovers = useCategoryCoverImagesQuery(coverCategoryIds);
  const clientLogosQuery = useClientLogosQuery();
  const testimonialsQuery = useLandingTestimonialsQuery();
  const partnersQuery = useLandingPartnersQuery();
  const heroRef = useRef<HTMLDivElement>(null);
  // 히어로를 거의 다 지나면(작은 로고 줄 높이 80px 남기고) 로고 줄을 흰 바탕으로 바꾼다.
  const isPastHero = useScrolledAbove(heroRef, 80);

  return (
    // 한국어는 어절 단위로 줄을 바꾼다 — 단어 중간에서 끊기지 않게.
    <div className="flex min-h-dvh flex-col bg-background break-keep">
      <SiteHeader variant="overlay" isOverContent={isPastHero} />

      <main className="flex flex-1 flex-col gap-24 pb-24 sm:gap-32 sm:pb-32">
        <div ref={heroRef}>
          <HomeHero
            banners={bannersQuery.data ?? []}
            isBannersPending={bannersQuery.isPending}
            products={products}
            isPending={productsQuery.isPending}
            isError={productsQuery.isError}
            onRetry={() => {
              void productsQuery.refetch();
            }}
            nextSectionId="home-bestsellers"
          />
        </div>

        <HomeBestsellers products={products} />
        <HomeCategories
          groups={categoryGroups}
          covers={categoryCovers}
          isPending={categoriesQuery.isPending}
          isError={categoriesQuery.isError}
          onRetry={() => {
            void categoriesQuery.refetch();
          }}
        />
        <HomeFeatured products={products} />
        <HomePortfolio
          items={portfolioQuery.data ?? []}
          isPending={portfolioQuery.isPending}
          isError={portfolioQuery.isError}
          onRetry={() => {
            void portfolioQuery.refetch();
          }}
        />
        <HomeTestimonials items={testimonialsQuery.data ?? []} />
        {/* 로고는 후기 바로 아래 붙인다 — 실패·로딩은 조용히 생략한다(장식 성격). */}
        <HomeClientLogos items={clientLogosQuery.data ?? []} partners={partnersQuery.data ?? []} />
      </main>

      {/* 마무리 브랜드 배너는 푸터 바로 위에 붙인다(main 의 아래 여백 밖). */}
      <HomeBrandBanner products={products} />

      <SiteFooter />
    </div>
  );
};
